<template>
  <div class="mt-4  px-5">

    <h2>Schiessbuch</h2>

    <v-alert
        dense
        text
        type="success">
      <b>Tipp:</b>
      <div>
        Hier werden alle beendeten Einträge von euren Schützen angezeigt. <br>
        <br><i class="fas fa-user-graduate"></i> Euer Schuetzenbuch könnt Ihr exportieren, falls Ihr es mal bei einem
        Amt vorlegen möchtet.
      </div>
    </v-alert>

    <!-- Vereins-Auswahl für User mit mehreren VAdmin-Vereinen -->
    <v-select
        v-if="comp_isUserLoggedIn && comp_vadminClubs.length > 1"
        v-model="selected_club_id"
        :items="comp_vadminClubs"
        item-text="name"
        item-value="id"
        label="Verein auswählen"
        outlined
        dense
        class="mb-4"
        @change="getMyShootingbook"
    ></v-select>

    <div class="d-flex align-center mb-4 flex-wrap" style="gap: 12px;">
      <span class="caption grey--text">Zeilenfarben:</span>
      <v-chip x-small class="amber lighten-4">
        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
        Zur Genehmigung eingereicht
      </v-chip>
      <v-chip x-small class="red lighten-5 error--text">
        <v-icon x-small class="mr-1" color="error">mdi-alert-circle-outline</v-icon>
        Abgelehnt
      </v-chip>
    </div>

    <v-data-table
        :headers="headers"
        :items="comp_filteredShootingbook"
        sort-by="beginn"
        :sort-desc="[true]"
        class="elevation-1"
        ref="Shootingbook"
        :item-class="itemRowClass"
    >
      <template v-slot:top>
        <v-toolbar flat dense color="success" dark>
          <v-toolbar-title>Schiessbuch</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-switch
              v-model="export_all"
              :label="export_all ? 'Export: Alle' : 'Export: Gefiltert'"
              dense
              hide-details
              class="mt-0 mr-2"
          ></v-switch>
          <shootingbook-p-d-f-export
              :pdf_data="comp_exportData"
              :name="comp_ClubName"
              club_user="club"
          ></shootingbook-p-d-f-export>
          <shootingbook-excel-export
              :excel_data="comp_exportData"
              :name="comp_ClubName"
              club_user="club"
          ></shootingbook-excel-export>
          <v-btn icon @click="getMyShootingbook">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="padding: 12px 16px; border-bottom: 1px solid rgba(0,0,0,.12);">
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="3">
              <v-text-field
                  v-model="search"
                  label="Suche (Name, Waffe, Stand, ...)"
                  prepend-inner-icon="mdi-magnify"
                  outlined dense clearable hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="2">
              <v-select
                  v-model="filter_status"
                  :items="statusOptions"
                  label="Status"
                  outlined dense hide-details clearable
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field
                  v-model="date_from"
                  label="Von"
                  type="date"
                  outlined dense hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field
                  v-model="date_to"
                  label="Bis"
                  type="date"
                  outlined dense hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center">
              <v-btn text small @click="resetFilters">
                <v-icon left small>mdi-filter-off</v-icon>
                Zurücksetzen
              </v-btn>
              <span class="caption grey--text ml-2">
                {{ comp_filteredShootingbook.length }}/{{ comp_Shootingbook.length }}
              </span>
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-slot:item.shooting_function_name="{ item }">
        <span>{{ item.shooting_function_name || item.Aufsicht || item.funktion_name || (isNaN(item.funktion) ? item.funktion : getFunctionName(item.shooting_function_id || item.funktion)) }}</span>
      </template>

      <template v-slot:item.beginn="{ item }">
        <span>{{
            new Date(item.beginn.replace(" ", "T")).toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short'
            })
          }}</span>
      </template>
      <template v-slot:item.ende="{ item }">
        <span>{{
            new Date(item.ende.replace(" ", "T")).toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short'
            })
          }}</span>
      </template>


      <template v-slot:item.bestaetigt_vereinDatum="{ item }">
        <span v-if="item.bestaetigt_vereinDatum">
          {{ new Date(item.bestaetigt_vereinDatum.replace(" ", "T")).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) }}
        </span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.approved_from_name="{ item }">
        <span v-if="item.approved_from_name">{{ item.approved_from_name }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:no-data>
        <v-btn
            color="primary"
            @click="initialize"
        >
          neu laden
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import shootingbookExcelExport from '@/views/component/shootingbookExcelExport';
import ShootingbookPDFExport from "@/views/component/shootingbookPDFExport";

const today = new Date();
const defaultFrom = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
const defaultTo   = today.toISOString().split('T')[0];

export default {
  name: 'shootingbook',
  components: {ShootingbookPDFExport, shootingbookExcelExport},
  data: () => ({
    selected_club_id: null,
    search: '',
    filter_status: null,
    date_from: defaultFrom,
    date_to: defaultTo,
    export_all: false,
    statusOptions: [
      { text: 'Nicht eingereicht', value: 0 },
      { text: 'Eingereicht',       value: 1 },
      { text: 'Freigegeben',       value: 2 },
      { text: 'Abgelehnt',         value: 3 },
    ],
    headers: [
      {
        align: 'Name',
        sortable: false,
      },
      {text: 'Name', value: 'name'},
      {text: 'Vorname', value: 'vorname'},
      {text: 'Stand', value: 'KurzBez'},
      {text: 'Bahn', value: 'bahn'},
      {text: 'Funktion', value: 'shooting_function_name'},
      {text: 'Waffe - Kurztext', value: 'KurzT'},
      {text: 'Disziplin', value: 'LangBez'},
      {text: 'Ergebnis', value: 'trergges'},
      {text: 'Begin', value: 'beginn'},
      {text: 'Ende', value: 'ende'},
      {text: 'Freigegeben am', value: 'bestaetigt_vereinDatum', sortable: true},
      {text: 'Freigegeben von', value: 'approved_from_name', sortable: false},
    ],

  }),

  computed: {
    comp_Shootingbook() {
      return this.$store.state.ClubApp.Shootingbook;
    },
    comp_filteredShootingbook() {
      let data = this.comp_Shootingbook;

      if (this.search && this.search.trim()) {
        const s = this.search.toLowerCase().trim();
        data = data.filter(item =>
          (item.name                  && item.name.toLowerCase().includes(s)) ||
          (item.vorname               && item.vorname.toLowerCase().includes(s)) ||
          (item.KurzBez               && item.KurzBez.toLowerCase().includes(s)) ||
          (item.KurzT                 && item.KurzT.toLowerCase().includes(s)) ||
          (item.LangBez               && item.LangBez.toLowerCase().includes(s)) ||
          (item.shooting_function_name && item.shooting_function_name.toLowerCase().includes(s)) ||
          (item.Aufsicht              && item.Aufsicht.toLowerCase().includes(s)) ||
          (item.funktion_name         && item.funktion_name.toLowerCase().includes(s)) ||
          (item.aktivitaet            && item.aktivitaet.toLowerCase().includes(s))
        );
      }

      if (this.filter_status !== null && this.filter_status !== undefined) {
        data = data.filter(item => item.bestaetigung_status === this.filter_status);
      }

      if (this.date_from) {
        const from = new Date(this.date_from + 'T00:00:00');
        data = data.filter(item => new Date(item.beginn.replace(' ', 'T')) >= from);
      }

      if (this.date_to) {
        const to = new Date(this.date_to + 'T23:59:59');
        data = data.filter(item => new Date(item.beginn.replace(' ', 'T')) <= to);
      }

      return data;
    },
    comp_exportData() {
      return this.export_all ? this.comp_Shootingbook : this.comp_filteredShootingbook;
    },
    comp_isUserLoggedIn() {
      return this.$store.getters['UserAuth/isLoggedIn'];
    },
    comp_vadminClubs() {
      const clubs = this.$store.state.UserApp.Clubs;
      if (clubs && clubs.meineVereine) {
        return clubs.meineVereine.filter(v => v.pivot && v.pivot.isVAdmin === 1);
      }
      return [];
    },
    comp_ClubName() {
      const club = this.$store.getters['ClubAuth/isClubName'];
      const userClub = this.$store.state.UserAuth.defClub;
      var data = null;
      data = ''
      if (typeof club !== 'undefined') {
        data = club;
      } else if (userClub !== null) {
        data = userClub;
      }
      return data
    }

  },

  mounted() {
    if (this.comp_isUserLoggedIn) {
      this.tryAutoSelectClub();
    } else {
      this.getMyShootingbook();
    }
  },

  watch: {
    comp_vadminClubs() {
      if (this.comp_isUserLoggedIn && !this.selected_club_id) {
        this.tryAutoSelectClub();
      }
    },
  },

  methods: {
    tryAutoSelectClub() {
      const clubs = this.comp_vadminClubs;
      if (clubs.length === 1) {
        this.selected_club_id = clubs[0].id;
        this.getMyShootingbook();
      }
      // Bei mehreren Vereinen: Benutzer wählt im Dropdown
    },

    getMyShootingbook() {
      const club_id = this.comp_isUserLoggedIn ? this.selected_club_id : null;
      this.$store.dispatch('ClubApp/getShootingbook', club_id);
    },
    getFunctionName(id) {
      const map = { 1: "Schütze", 2: "Standaufsicht", 3: "Schießsportleiter" };
      return map[id] || "Unbekannt";
    },
    initialize() {
      this.getMyShootingbook();
    },
    itemRowClass(item) {
      if (item.bestaetigung_status === 1) {
        return 'amber lighten-4';
      } else if (item.bestaetigung_status === 3) {
        return 'red lighten-5';
      }
      return '';
    },
    resetFilters() {
      this.search = '';
      this.filter_status = null;
      this.date_from = defaultFrom;
      this.date_to = defaultTo;
    },
  },
}
</script>

<style scoped>


</style>
